import { useEffect, useState } from 'react'
import { useQuery, type UseQueryResult } from '@tanstack/react-query'

import { useApiClient } from '@/api/ApiClientProvider'
// The predicate lives beside `MODULE_FLAGS`, the mapping it reads, so the plan
// and the gates cannot disagree about which modules a flag gates.
import { isFlagGatedModule } from '@/api/overview'
import type { ModuleKey, Permission } from '@/api/types'
import { isRetryableError } from '@/lib/queryClient'

const SESSION_STALE_MS = 5 * 60_000

export function useSession() {
  const client = useApiClient()
  return useQuery({
    queryKey: ['session', client.kind],
    queryFn: () => client.session(),
    staleTime: SESSION_STALE_MS,
  })
}

export function useCurrentTenant() {
  const client = useApiClient()
  return useQuery({
    queryKey: ['tenant', client.kind],
    queryFn: () => client.currentTenant(),
    staleTime: SESSION_STALE_MS,
  })
}

/** The query's error, or the last one it had while a refetch has it cleared. */
function useLastError(error: unknown): unknown {
  const [last, setLast] = useState<unknown>(null)
  useEffect(() => {
    if (error !== null) setLast(error)
  }, [error])
  return error ?? last
}

/**
 * A read the gates wait on, with its failure kept across a retry.
 *
 * TanStack puts a query with nothing read back to `pending`, with its `error`
 * cleared, while it is asked again. Taken at face value, every retry would
 * read as a first load — swapping pages the failure does not concern for a
 * loading state — and a refusal would lose the error that makes it one. So a
 * read has failed once it errored with nothing read, until it answers, and
 * keeps its last error meanwhile. A read that failed after answering keeps
 * its answer instead (`isFailed` is false).
 */
function useGateRead(query: UseQueryResult<unknown>) {
  const failed = query.data === undefined && query.errorUpdateCount > 0
  const error = useLastError(query.error)
  return {
    isPending: query.isPending && !failed,
    isFailed: failed,
    isRetrying: failed && query.isFetching,
    /**
     * Failed in a way asking again may fix (`isRetryableError`). A component
     * that mounts while a failed read is asked again never saw its error, and
     * counts it as retryable until the answer comes back.
     */
    retryable: failed && (error === null || isRetryableError(error)),
    /** Asked again since it first failed, and failed again. */
    retryFailed: failed && query.errorUpdateCount > 1,
    refetch: query.refetch,
  }
}

/**
 * Whether a plan module is open to this tenant: `pending` until the plan is
 * read, `unknown` when the read failed and the plan could switch it off.
 */
export type ModuleAccess = 'enabled' | 'disabled' | 'pending' | 'unknown'

/**
 * The tenant's plan modules (pivot doc §5, `entitlements`). A module that is
 * `disabled` is shown as locked in navigation and refused at its routes.
 * `null` means "not a plan module" (Settings, Integrations) and is always on.
 *
 * Only a module the plan can switch off (`isFlagGatedModule`) waits on the
 * read; any other is on before it answers, or if it fails. When it fails, a
 * gated module is `unknown` — never granted, never called "not in your plan" —
 * until `refetch` answers.
 */
export function useEntitlements() {
  const client = useApiClient()
  const query = useQuery({
    queryKey: ['entitlements', client.kind],
    queryFn: () => client.entitlements(),
    staleTime: SESSION_STALE_MS,
  })
  const read = useGateRead(query)
  const moduleAccess = (key: ModuleKey | null): ModuleAccess => {
    if (key === null) return 'enabled'
    if (query.data) return query.data.modules[key] ? 'enabled' : 'disabled'
    if (!isFlagGatedModule(key)) return 'enabled'
    return read.isFailed ? 'unknown' : 'pending'
  }
  return {
    ...read,
    modules: query.data?.modules,
    moduleAccess,
  }
}

/** A read the gates wait on (`useEntitlements`, `usePermissions`). */
export type GateRead = ReturnType<typeof useGateRead>

/**
 * The caller's RBAC permissions, as the server resolved them. The app hides
 * what the API would refuse; the API still refuses it.
 */
export function usePermissions() {
  const session = useSession()
  const granted = session.data?.permissions ?? []
  return {
    ...useGateRead(session),
    role: session.data?.role ?? null,
    can: (permission: Permission | null | undefined) => !permission || granted.includes(permission),
  }
}
